import { useEffect, useId, useRef, useState } from "react";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { OnboardingAnswers } from "../../../types/onboarding";
import styles from "./HowDidYouHearQuestion.module.css";

interface HowDidYouHearQuestionProps {
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | string[]) => void;
	showValidation?: boolean;
}

export function HowDidYouHearQuestion({
	answers,
	onInputChange,
	showValidation = false,
}: HowDidYouHearQuestionProps) {
	const { t, direction } = useClientTranslations("onboarding");
	const { logEvent } = useAnalytics();
	const groupId = useId();
	const hasInitialized = useRef(false);

	const currentSelections = Array.isArray(answers.howDidYouHearAboutUs)
		? answers.howDidYouHearAboutUs
		: [];

	// Extract existing "Other - ..." text if present
	const existingOtherEntry = currentSelections.find((v) =>
		v.startsWith("Other - "),
	);
	const initialOtherText = existingOtherEntry
		? existingOtherEntry.replace("Other - ", "")
		: "";

	const [otherText, setOtherText] = useState(initialOtherText);

	// Initialize otherText from existing data on mount
	useEffect(() => {
		if (!hasInitialized.current && initialOtherText) {
			setOtherText(initialOtherText);
			hasInitialized.current = true;
		}
	}, [initialOtherText]);

	const handleToggleOption = (value: string) => {
		// Remove any existing "Other" or "Other - ..." entries
		const selectionsWithoutOther = currentSelections.filter(
			(v) => v !== "Other" && !v.startsWith("Other - "),
		);

		const isCurrentlySelected =
			currentSelections.includes(value) ||
			currentSelections.some((v) => v.startsWith("Other - "));

		let newSelections: string[];
		if (value === "Other") {
			if (isCurrentlySelected) {
				// Deselecting "Other"
				newSelections = selectionsWithoutOther;
				setOtherText("");
			} else {
				// Selecting "Other"
				newSelections = [...selectionsWithoutOther, value];
			}
		} else {
			// Toggle non-Other options normally
			newSelections = currentSelections.includes(value)
				? currentSelections.filter((v) => v !== value)
				: [...currentSelections, value];
		}

		onInputChange("howDidYouHearAboutUs", newSelections);

		logEvent(
			isCurrentlySelected
				? "how_did_you_hear_deselected"
				: "how_did_you_hear_selected",
			undefined,
			{
				field_name: "howDidYouHearAboutUs",
				question_type: "how-did-you-hear",
				selected_value: value,
				total_selected: newSelections.length,
			},
		);
	};

	const handleOtherTextChange = (text: string) => {
		setOtherText(text);

		// Update the array: remove old "Other" or "Other - ..." and add new "Other - <text>"
		const selectionsWithoutOther = currentSelections.filter(
			(v) => v !== "Other" && !v.startsWith("Other - "),
		);

		const newValue = text.trim() ? `Other - ${text.trim()}` : "Other";
		const newSelections = [...selectionsWithoutOther, newValue];

		onInputChange("howDidYouHearAboutUs", newSelections);

		logEvent("how_did_you_hear_other_text_entered", undefined, {
			field_name: "howDidYouHearAboutUs",
			question_type: "how-did-you-hear",
			text_length: text.length,
		});
	};

	const hasError = showValidation && currentSelections.length === 0;

	const referralOptions = [
		{
			value: "Through my clinic or doctor",
			label: t("howDidYouHear.options.clinic"),
		},
		{
			value: "From a friend or family member",
			label: t("howDidYouHear.options.friend"),
		},
		{ value: "On social media", label: t("howDidYouHear.options.socialMedia") },
		{ value: "Online search", label: t("howDidYouHear.options.onlineSearch") },
		{ value: "Other", label: t("howDidYouHear.options.other") },
	];

	return (
		<div className={styles.container} dir={direction}>
			<fieldset className={styles.optionsList} id={groupId}>
				{referralOptions.map((option) => {
					const isSelected =
						option.value === "Other"
							? currentSelections.includes("Other") ||
								currentSelections.some((v) => v.startsWith("Other - "))
							: currentSelections.includes(option.value);
					return (
						<label
							key={option.value}
							className={`${styles.optionCard} ${
								isSelected ? styles.optionCardSelected : ""
							} ${hasError ? styles.optionCardError : ""}`}
						>
							<input
								type="checkbox"
								checked={isSelected}
								onChange={() => handleToggleOption(option.value)}
								className={styles.hiddenCheckbox}
								aria-label={option.label}
							/>
							<div
								className={`${styles.checkbox} ${
									isSelected ? styles.checkboxSelected : ""
								}`}
							>
								{isSelected && (
									<svg
										width="12"
										height="9"
										viewBox="0 0 12 9"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										className={styles.checkmark}
										role="img"
										aria-label="Selected"
									>
										<path
											d="M1 4.5L4.5 8L11 1"
											stroke="white"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								)}
							</div>
							<span className={styles.optionLabel}>{option.label}</span>
						</label>
					);
				})}
			</fieldset>
			{(currentSelections.includes("Other") ||
				currentSelections.some((v) => v.startsWith("Other - "))) && (
				<div className={styles.otherInputWrapper}>
					<input
						type="text"
						value={otherText}
						onChange={(e) => handleOtherTextChange(e.target.value)}
						placeholder={t("howDidYouHear.otherPlaceholder")}
						className={styles.otherInput}
						aria-label={t("howDidYouHear.otherPlaceholder")}
					/>
				</div>
			)}
		</div>
	);
}
